import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Box, Typography } from '@material-ui/core'
import { PlayArrow } from '@material-ui/icons'

// Import presentation images
import observabilityImg from '../../assets/img/cyberpunk/resume/observability.png'
import contractDrivenImg from '../../assets/img/cyberpunk/resume/contract_driven.png'
import projectLeadershipImg from '../../assets/img/cyberpunk/resume/project_leadership.png'
import persuasionImg from '../../assets/img/cyberpunk/resume/persuasion.png'
import technicalCommunicationImg from '../../assets/img/cyberpunk/resume/technical_communication.png'
import sqlDbImg from '../../assets/img/cyberpunk/resume/sql_db.png'

const PresentationsGallery = ({ presentations }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)

  const presentationCategories = {
    'OpenTelemetry': { icon: observabilityImg, color: '#00ffff', category: 'Observability' },
    'Contract Driven Development': { icon: contractDrivenImg, color: '#ff00ff', category: 'Architecture' },
    'Projects': { icon: projectLeadershipImg, color: '#00ff88', category: 'Leadership' },
    'Persuasion': { icon: persuasionImg, color: '#ffaa00', category: 'Leadership' },
    'Communications': { icon: technicalCommunicationImg, color: '#ff0088', category: 'Leadership' },
    'SQL': { icon: sqlDbImg, color: '#00aaff', category: 'Technical' }
  }

  const getMetadata = (title) => {
    for (const [key, meta] of Object.entries(presentationCategories)) {
      if (title.includes(key)) return meta
    }
    return { icon: '📄', color: '#00ffff', category: 'Technical' }
  }

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        style={{ marginBottom: '0rem', textAlign: 'center' }}
      >
        <Typography style={{
          fontFamily: '"Share Tech Mono", monospace',
          fontSize: '0.9rem',
          color: '#888',
          letterSpacing: '0.2em'
        }}>
          TECHNICAL PRESENTATIONS & THOUGHT LEADERSHIP
        </Typography>
      </motion.div>

      <Box style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '2rem',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {presentations.map((presentation, index) => {
          const metadata = getMetadata(presentation.title)
          const isHovered = hoveredIndex === index

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, rotateY: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{ perspective: '1000px' }}
            >
              <motion.div
                whileHover={{
                  scale: 1.05,
                  rotateY: 5,
                  z: 50
                }}
                transition={{ duration: 0.3 }}
                onClick={() => window.open(presentation.url, '_blank')}
                style={{
                  background: isHovered
                    ? `linear-gradient(135deg, ${metadata.color}20 0%, rgba(0, 0, 0, 0.95) 100%)`
                    : 'linear-gradient(135deg, rgba(0, 20, 30, 0.95) 0%, rgba(20, 0, 30, 0.95) 100%)',
                  border: `2px solid ${isHovered ? metadata.color : `${metadata.color}40`}`,
                  borderRadius: '16px',
                  padding: '2rem',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  backdropFilter: 'blur(10px)',
                  boxShadow: isHovered
                    ? `0 20px 60px ${metadata.color}40, inset 0 0 30px ${metadata.color}10`
                    : '0 10px 30px rgba(0, 0, 0, 0.5)',
                  transition: 'all 0.3s ease',
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* Animated corner accent */}
                <Box style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: `linear-gradient(135deg, ${metadata.color}30 0%, transparent 100%)`,
                  clipPath: 'polygon(100% 0, 100% 100%, 0 0)',
                  opacity: isHovered ? 1 : 0.3,
                  transition: 'opacity 0.3s ease'
                }}/>

                {/* Category badge */}
                <Box style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem',
                  background: metadata.color,
                  color: '#000',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.7rem',
                  fontFamily: '"Share Tech Mono", monospace',
                  fontWeight: 'bold',
                  boxShadow: `0 0 20px ${metadata.color}80`
                }}>
                  {metadata.category}
                </Box>

                {/* Icon */}
                <Box style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '1.5rem',
                  marginBottom: '1rem'
                }}>
                  <motion.div
                    animate={isHovered ? {
                      scale: [1, 1.08, 1]
                    } : {}}
                    transition={{ duration: 0.6 }}
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      opacity: isHovered ? 0.85 : 0.6,
                      transition: 'opacity 0.3s ease',
                      filter: `drop-shadow(0 0 15px ${metadata.color}40)`
                    }}
                  >
                    <img
                      src={metadata.icon}
                      alt={presentation.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '12px'
                      }}
                    />
                  </motion.div>
                </Box>

                {/* Title */}
                <Typography style={{
                  fontFamily: '"Orbitron", monospace',
                  fontSize: '1.15rem',
                  color: isHovered ? metadata.color : '#ffffff',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  lineHeight: 1.35,
                  marginBottom: '1rem',
                  textShadow: isHovered ? `0 0 25px ${metadata.color}` : '0 0 8px rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  letterSpacing: '0.01em'
                }}>
                  {presentation.title}
                </Typography>

                {/* View button */}
                <Box style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  marginTop: 'auto'
                }}>
                  <motion.div
                    animate={isHovered ? { x: [0, 5, 0] } : {}}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  >
                    <PlayArrow style={{
                      color: metadata.color,
                      fontSize: '1.5rem',
                      filter: `drop-shadow(0 0 10px ${metadata.color})`
                    }}/>
                  </motion.div>
                  <Typography style={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: '0.85rem',
                    color: metadata.color,
                    fontWeight: '600',
                    textShadow: `0 0 10px ${metadata.color}80`
                  }}>
                    View Presentation
                  </Typography>
                </Box>

                {/* Scan line effect */}
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      initial={{ top: '-100%' }}
                      animate={{ top: '200%' }}
                      exit={{ top: '200%' }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: `linear-gradient(90deg, transparent 0%, ${metadata.color} 50%, transparent 100%)`,
                        boxShadow: `0 0 20px ${metadata.color}`,
                        pointerEvents: 'none'
                      }}
                    />
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          )
        })}
      </Box>
    </Box>
  )
}

export default PresentationsGallery
